import React, { useEffect, useMemo, useState } from 'react';
import { planApi, formatMoney, formatDateTime, fromMinor, toMinor, INTERVAL_LABELS, formatBytes } from './planApi';
import { Badge, ConfirmModal, Field } from './PlanUi';

const MB = 1024 * 1024;
const ON_EXCEED_LABELS = {
  block: 'Block', warn: 'Warn only', soft_limit: 'Soft limit', paid_overage: 'Paid overage', upgrade_required: 'Prompt upgrade',
};
const PERIOD_LABELS = { calendar_month: 'Calendar month', billing_period: 'Billing period', day: 'Day' };

// Byte-valued entitlements are edited in MB for readability
const toDisplay = (def, value) => (def.unit === 'bytes' ? +(value / MB).toFixed(2) : value);
const fromDisplay = (def, value) => {
  if (value === '' || value === null || value === undefined) return 0;
  return def.unit === 'bytes' ? Math.round(Number(value) * MB) : Number(value);
};

// ─── version document ⇄ editable form ────────────────────────────────────────
const toForm = (version, registry) => {
  const byKey = (arr) => Object.fromEntries((arr || []).map(x => [x.key, x]));
  const f = byKey(version.entitlements?.features);
  const l = byKey(version.entitlements?.limits);
  const q = byKey(version.entitlements?.quotas);
  const p = byKey(version.entitlements?.policies);

  return {
    identity: {
      name: version.identity?.name || '',
      description: version.identity?.description || '',
      category: version.identity?.category || 'self_serve',
      displayOrder: version.identity?.displayOrder ?? 0,
      isPublic: version.identity?.isPublic !== false,
      isFeatured: !!version.identity?.isFeatured,
      badges: (version.identity?.badges || []).join(', '),
    },
    pricing: {
      currency: version.pricing?.currency || 'INR',
      billingOptions: (version.pricing?.billingOptions || []).map(o => ({
        interval: o.interval,
        amount: fromMinor(o.amountMinor),
        setupFee: fromMinor(o.setupFeeMinor || 0),
        compareAt: fromMinor(o.compareAtMinor),
        isActive: o.isActive !== false,
      })),
      taxMode: version.pricing?.tax?.mode || 'exclusive',
      gstRatePercent: version.pricing?.tax?.gstRatePercent ?? 18,
      sacCode: version.pricing?.tax?.sacCode || '',
      trialDays: version.pricing?.trialDays ?? 0,
      gracePeriodDays: version.pricing?.gracePeriodDays ?? 3,
      allowCoupons: version.pricing?.allowCoupons !== false,
    },
    features: Object.fromEntries(registry.features.map(d => [d.key, !!f[d.key]?.enabled])),
    limits: Object.fromEntries(registry.limits.map(d => [d.key, {
      value: toDisplay(d, l[d.key]?.value ?? 0),
      unlimited: !!l[d.key]?.unlimited,
      onExceed: l[d.key]?.onExceed || 'block',
    }])),
    quotas: Object.fromEntries(registry.quotas.map(d => [d.key, {
      value: toDisplay(d, q[d.key]?.value ?? 0),
      unlimited: !!q[d.key]?.unlimited,
      period: q[d.key]?.period || d.period,
      onExceed: q[d.key]?.onExceed || 'block',
    }])),
    policies: Object.fromEntries(registry.policies.map(d => [d.key, p[d.key] ? p[d.key].value : d.default])),
    highlights: (version.presentation?.highlights || []).join('\n'),
    termsVersion: version.presentation?.termsVersion || '1',
    effectiveFrom: version.lifecycle?.effectiveFrom ? version.lifecycle.effectiveFrom.slice(0, 10) : '',
    effectiveUntil: version.lifecycle?.effectiveUntil ? version.lifecycle.effectiveUntil.slice(0, 10) : '',
    changeNote: version.changeNote || '',
  };
};

const toPayload = (form, registry, version) => ({
  expectedUpdatedAt: version.updatedAt,
  identity: {
    ...form.identity,
    displayOrder: Number(form.identity.displayOrder) || 0,
    badges: form.identity.badges.split(',').map(s => s.trim()).filter(Boolean),
  },
  pricing: {
    currency: form.pricing.currency,
    billingOptions: form.pricing.billingOptions.map(o => ({
      interval: o.interval,
      amountMinor: toMinor(o.amount === '' ? 0 : o.amount),
      setupFeeMinor: toMinor(o.setupFee === '' ? 0 : o.setupFee),
      compareAtMinor: o.compareAt === '' ? null : toMinor(o.compareAt),
      isActive: o.isActive,
    })),
    tax: { mode: form.pricing.taxMode, gstRatePercent: Number(form.pricing.gstRatePercent), sacCode: form.pricing.sacCode || null },
    trialDays: Number(form.pricing.trialDays) || 0,
    gracePeriodDays: Number(form.pricing.gracePeriodDays) || 0,
    allowCoupons: form.pricing.allowCoupons,
  },
  entitlements: {
    features: registry.features.map(d => ({ key: d.key, enabled: !!form.features[d.key] })),
    limits: registry.limits.map(d => ({
      key: d.key,
      value: fromDisplay(d, form.limits[d.key].value),
      unlimited: form.limits[d.key].unlimited,
      onExceed: form.limits[d.key].onExceed,
    })),
    quotas: registry.quotas.map(d => ({
      key: d.key,
      value: fromDisplay(d, form.quotas[d.key].value),
      unlimited: form.quotas[d.key].unlimited,
      period: form.quotas[d.key].period,
      onExceed: form.quotas[d.key].onExceed,
    })),
    policies: registry.policies.map(d => ({ key: d.key, value: form.policies[d.key] })),
  },
  presentation: {
    highlights: form.highlights.split('\n').map(s => s.trim()).filter(Boolean),
    termsVersion: form.termsVersion,
  },
  lifecycle: { effectiveFrom: form.effectiveFrom || null, effectiveUntil: form.effectiveUntil || null },
  changeNote: form.changeNote,
});

// ─── Sub-components ──────────────────────────────────────────────────────────
const Section = ({ title, subtitle, children, defaultOpen = true, aside }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`pm-section ${open ? 'pm-section--open' : ''}`}>
      {/* div, not button: must stay clickable inside a disabled (read-only) fieldset */}
      <div
        role="button"
        tabIndex={0}
        className="pm-section__head"
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(o => !o))}
      >
        <span className="pm-section__chev">{open ? '▾' : '▸'}</span>
        <span className="pm-section__title">{title}</span>
        {subtitle && <span className="pm-section__sub">{subtitle}</span>}
        {aside && <span className="pm-section__aside">{aside}</span>}
      </div>
      {open && <div className="pm-section__body">{children}</div>}
    </section>
  );
};

const Toggle = ({ checked, onChange, disabled, label }) => (
  <label className={`pm-toggle ${disabled ? 'pm-toggle--disabled' : ''}`}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
    <span className="pm-toggle__track"><span className="pm-toggle__thumb" /></span>
    {label && <span className="pm-toggle__label">{label}</span>}
  </label>
);

// ─── Consistency (same rules as backend services/planConsistency.js) ─────────
const requiredFeatures = (def) => (!def?.requiresFeature ? [] : Array.isArray(def.requiresFeature) ? def.requiresFeature : [def.requiresFeature]);

/** Per limit/quota key: { tone: 'error' | 'warn' | 'muted', text, inactive } */
const checkForm = (form, registry) => {
  const notes = {};
  const featureLabel = (k) => registry.features.find(f => f.key === k)?.label || k;
  const inspect = (defs, entries, kind) => defs.forEach(def => {
    const e = entries[def.key];
    if (!e) return;
    const needs = requiredFeatures(def);
    const featureOn = !needs.length || needs.some(f => form.features[f]);
    const granted = e.unlimited || Number(e.value) > 0;
    const needText = needs.map(featureLabel).join(' / ');
    if (!featureOn && granted) {
      notes[def.key] = { tone: 'error', inactive: true, text: `${needText} is off, so this can't be used. Turn the feature on or set this to 0.` };
    } else if (!featureOn) {
      notes[def.key] = { tone: 'muted', inactive: true, text: `Applies only when ${needText} is on.` };
    } else if (def.available === false) {
      notes[def.key] = { tone: 'muted', text: 'Coming soon: shown on plans but not enforced yet.' };
    } else if (!e.unlimited && Number(e.value) === 0) {
      notes[def.key] = { tone: 'warn', text: `0 means partners can't ${kind === 'quota' ? 'use this at all' : 'add any'}. Tick Unlimited if that isn't intended.` };
    }
  });
  inspect(registry.limits, form.limits, 'limit');
  inspect(registry.quotas, form.quotas, 'quota');
  return notes;
};

const QuantityRow = ({ def, entry, onChange, readOnly, kind, registry, note }) => (
  <div className={`pm-qrow ${note?.inactive ? 'pm-qrow--inactive' : ''}`}>
    <div className="pm-qrow__label">
      <span>
        {def.label}
        {def.available === false && <span className="pm-badge pm-badge--muted pm-qrow__badge">Coming soon</span>}
      </span>
      <span className="pm-qrow__meta">
        {kind === 'limit' ? `per ${def.scope}` : 'usage quota'} · {def.unit === 'bytes' ? 'MB' : def.unit}
      </span>
      {note && <span className={`pm-qrow__note pm-qrow__note--${note.tone}`}>{note.text}</span>}
    </div>
    <div className="pm-qrow__controls">
      <input
        type="number"
        min="0"
        step={def.unit === 'bytes' ? '0.01' : '1'}
        className="pm-input pm-input--num"
        value={entry.unlimited ? '' : entry.value}
        placeholder={entry.unlimited ? '∞' : '0'}
        disabled={readOnly || entry.unlimited}
        onChange={e => onChange({ ...entry, value: e.target.value })}
      />
      <label className="pm-check">
        <input type="checkbox" checked={entry.unlimited} disabled={readOnly} onChange={e => onChange({ ...entry, unlimited: e.target.checked })} />
        Unlimited
      </label>
      {kind === 'quota' && (
        <select className="pm-input pm-input--sm" value={entry.period} disabled={readOnly} onChange={e => onChange({ ...entry, period: e.target.value })}>
          {registry.quotaPeriods.map(p => <option key={p} value={p}>{PERIOD_LABELS[p] || p}</option>)}
        </select>
      )}
      <select className="pm-input pm-input--sm" value={entry.onExceed} disabled={readOnly || entry.unlimited} onChange={e => onChange({ ...entry, onExceed: e.target.value })}>
        {registry.onExceed.map(o => <option key={o} value={o}>{ON_EXCEED_LABELS[o] || o}</option>)}
      </select>
    </div>
  </div>
);

const PolicyInput = ({ def, value, onChange, readOnly }) => {
  if (def.type === 'boolean') return <Toggle checked={value === true} onChange={onChange} disabled={readOnly} />;
  if (def.type === 'enum') {
    return (
      <select className="pm-input pm-input--sm" value={value ?? ''} disabled={readOnly} onChange={e => onChange(e.target.value)}>
        {def.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    );
  }
  return (
    <input type="number" min="0" className="pm-input pm-input--num" value={value ?? ''} placeholder="—" disabled={readOnly}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} />
  );
};

// ─── Main editor ─────────────────────────────────────────────────────────────
const PlanVersionEditor = ({ registry, planId, version, isCurrent, onChanged, notify }) => {
  const readOnly = version.status !== 'draft';
  const initial = useMemo(() => toForm(version, registry), [version, registry]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirm, setConfirm] = useState(null);   // 'publish' | 'delete' | 'archive'

  useEffect(() => { setForm(initial); setErrors([]); }, [initial]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  // Warn before leaving the page with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const set = (path, value) => setForm(prev => {
    const next = { ...prev };
    const keys = path.split('|');
    let node = next;
    for (let i = 0; i < keys.length - 1; i++) {
      node[keys[i]] = Array.isArray(node[keys[i]]) ? [...node[keys[i]]] : { ...node[keys[i]] };
      node = node[keys[i]];
    }
    node[keys[keys.length - 1]] = value;
    return next;
  });

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const res = await planApi.updateVersion(planId, version.version, toPayload(form, registry, version));
      notify('Draft saved');
      await onChanged(res.version.version);
      return true;
    } catch (err) {
      setErrors(err.data?.errors || [err.message]);
      notify(err.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    setSaving(true);
    try {
      if (confirm === 'publish') {
        if (dirty && !(await save())) { setSaving(false); setConfirm(null); return; }
        await planApi.publishVersion(planId, version.version);
        notify(`Version ${version.version} published`);
      } else if (confirm === 'delete') {
        await planApi.deleteVersion(planId, version.version);
        notify('Draft deleted');
        await onChanged(null);
        setConfirm(null);
        return;
      } else if (confirm === 'archive') {
        await planApi.archiveVersion(planId, version.version);
        notify(`Version ${version.version} archived`);
      }
      setConfirm(null);
      await onChanged(version.version);
    } catch (err) {
      setErrors(err.data?.errors || [err.message]);
      notify(err.message, 'error');
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const addBillingOption = () => {
    const used = new Set(form.pricing.billingOptions.map(o => o.interval));
    const interval = registry.billingIntervals.find(i => !used.has(i));
    if (!interval) return;
    set('pricing|billingOptions', [...form.pricing.billingOptions, { interval, amount: '', setupFee: 0, compareAt: '', isActive: true }]);
  };

  const updateBillingOption = (idx, patch) =>
    set('pricing|billingOptions', form.pricing.billingOptions.map((o, i) => (i === idx ? { ...o, ...patch } : o)));

  const groupContent = registry.groups.map(g => ({
    ...g,
    features: registry.features.filter(d => d.group === g.key),
    limits: registry.limits.filter(d => d.group === g.key),
    quotas: registry.quotas.filter(d => d.group === g.key),
    policies: registry.policies.filter(d => d.group === g.key),
  })).filter(g => g.features.length + g.limits.length + g.quotas.length + g.policies.length > 0);

  const enabledCount = Object.values(form.features).filter(Boolean).length;
  const notes = useMemo(() => checkForm(form, registry), [form, registry]);
  const conflicts = Object.entries(notes).filter(([, n]) => n.tone === 'error');
  const warnings = Object.entries(notes).filter(([, n]) => n.tone === 'warn');
  const labelOf = (key) => (registry.limits.find(d => d.key === key) || registry.quotas.find(d => d.key === key))?.label || key;

  return (
    <div className="pm-editor">
      <div className="pm-editor__head">
        <div>
          <div className="pm-editor__title">
            <h3>Version {version.version}</h3>
            <Badge status={version.status} />
            {isCurrent && <Badge tone="info">On sale</Badge>}
            {dirty && <Badge tone="warn">Unsaved changes</Badge>}
          </div>
          <p className="pm-muted pm-small">
            {version.status === 'draft' && `Draft · last saved ${formatDateTime(version.updatedAt)}`}
            {version.status === 'published' && `Published ${formatDateTime(version.lifecycle?.publishedAt)} · ${version.activeSubscribers || 0} active subscriber(s)`}
            {version.status === 'archived' && `Archived ${formatDateTime(version.lifecycle?.archivedAt)} · ${version.activeSubscribers || 0} subscriber(s) still on it`}
            {version.clonedFrom ? ` · cloned from v${version.clonedFrom}` : ''}
          </p>
        </div>
        <div className="pm-editor__actions">
          {version.status === 'draft' && (
            <>
              <button className="pm-btn pm-btn--ghost pm-btn--danger-text" onClick={() => setConfirm('delete')} disabled={saving}>Delete draft</button>
              <button className="pm-btn pm-btn--ghost" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save draft'}</button>
              <button className="pm-btn pm-btn--primary" onClick={() => setConfirm('publish')} disabled={saving || conflicts.length > 0}
                title={conflicts.length ? 'Fix the conflicting settings first' : undefined}>Publish</button>
            </>
          )}
          {version.status === 'published' && !isCurrent && (
            <button className="pm-btn pm-btn--ghost" onClick={() => setConfirm('archive')} disabled={saving}>Archive</button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="pm-callout">
          This version is {version.status} and cannot be edited — customers' contracts reference it. Use <strong>New draft</strong> to make changes.
        </div>
      )}

      {errors.length > 0 && (
        <div className="pm-callout pm-callout--error">
          <strong>Please fix the following:</strong>
          <ul>{errors.slice(0, 8).map(e => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="pm-callout pm-callout--error" role="alert">
          <strong>{conflicts.length === 1 ? 'One setting contradicts a feature' : `${conflicts.length} settings contradict their features`}{readOnly ? '' : ', so this version can’t be published yet'}:</strong>
          <ul>{conflicts.map(([key, n]) => <li key={key}><b>{labelOf(key)}</b>: {n.text}</li>)}</ul>
        </div>
      )}

      <fieldset className="pm-fieldset" disabled={readOnly}>
        <Section title="Identity" subtitle="Name, positioning and visibility">
          <div className="pm-grid pm-grid--2">
            <Field label="Display name">
              <input className="pm-input" value={form.identity.name} onChange={e => set('identity|name', e.target.value)} maxLength={80} />
            </Field>
            <Field label="Category" hint="Custom plans may be contract-priced (no list price)">
              <select className="pm-input" value={form.identity.category} onChange={e => set('identity|category', e.target.value)}>
                <option value="self_serve">Self-serve</option>
                <option value="sales_assisted">Sales-assisted</option>
                <option value="custom">Custom / Enterprise</option>
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea className="pm-input" rows={2} value={form.identity.description} onChange={e => set('identity|description', e.target.value)} maxLength={1000} />
          </Field>
          <div className="pm-grid pm-grid--3">
            <Field label="Display order">
              <input type="number" className="pm-input" value={form.identity.displayOrder} onChange={e => set('identity|displayOrder', e.target.value)} />
            </Field>
            <Field label="Badges" hint="Comma separated, e.g. Most popular">
              <input className="pm-input" value={form.identity.badges} onChange={e => set('identity|badges', e.target.value)} />
            </Field>
            <div className="pm-stack">
              <Toggle label="Public (shown on pricing page)" checked={form.identity.isPublic} onChange={v => set('identity|isPublic', v)} disabled={readOnly} />
              <Toggle label="Featured" checked={form.identity.isFeatured} onChange={v => set('identity|isFeatured', v)} disabled={readOnly} />
            </div>
          </div>
        </Section>

        <Section title="Pricing & billing" subtitle={form.pricing.billingOptions.map(o => `${INTERVAL_LABELS[o.interval]} ${formatMoney(toMinor(o.amount || 0), form.pricing.currency)}`).join(' · ') || 'No billing options'}>
          <div className="pm-billing">
            <div className="pm-billing__head">
              <span>Interval</span><span>Price (₹)</span><span>Compare-at (₹)</span><span>Setup fee (₹)</span><span>Active</span><span />
            </div>
            {form.pricing.billingOptions.map((o, idx) => (
              <div className="pm-billing__row" key={idx}>
                <select className="pm-input pm-input--sm" value={o.interval} onChange={e => updateBillingOption(idx, { interval: e.target.value })}>
                  {registry.billingIntervals.map(i => <option key={i} value={i}>{INTERVAL_LABELS[i]}</option>)}
                </select>
                <input type="number" min="0" step="0.01" className="pm-input pm-input--num" value={o.amount} onChange={e => updateBillingOption(idx, { amount: e.target.value })} />
                <input type="number" min="0" step="0.01" className="pm-input pm-input--num" value={o.compareAt} placeholder="—" onChange={e => updateBillingOption(idx, { compareAt: e.target.value })} />
                <input type="number" min="0" step="0.01" className="pm-input pm-input--num" value={o.setupFee} onChange={e => updateBillingOption(idx, { setupFee: e.target.value })} />
                <Toggle checked={o.isActive} onChange={v => updateBillingOption(idx, { isActive: v })} disabled={readOnly} />
                {!readOnly && (
                  <button type="button" className="pm-icon-btn" onClick={() => set('pricing|billingOptions', form.pricing.billingOptions.filter((_, i) => i !== idx))} aria-label="Remove">✕</button>
                )}
              </div>
            ))}
            {!readOnly && form.pricing.billingOptions.length < registry.billingIntervals.length && (
              <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={addBillingOption}>+ Add billing interval</button>
            )}
          </div>
          <div className="pm-grid pm-grid--4">
            <Field label="Tax treatment">
              <select className="pm-input" value={form.pricing.taxMode} onChange={e => set('pricing|taxMode', e.target.value)}>
                <option value="exclusive">GST exclusive</option>
                <option value="inclusive">GST inclusive</option>
              </select>
            </Field>
            <Field label="GST rate (%)">
              <input type="number" min="0" max="100" className="pm-input" value={form.pricing.gstRatePercent} onChange={e => set('pricing|gstRatePercent', e.target.value)} />
            </Field>
            <Field label="SAC code">
              <input className="pm-input" value={form.pricing.sacCode} onChange={e => set('pricing|sacCode', e.target.value)} placeholder="e.g. 998314" />
            </Field>
            <div className="pm-stack">
              <Toggle label="Allow coupons" checked={form.pricing.allowCoupons} onChange={v => set('pricing|allowCoupons', v)} disabled={readOnly} />
            </div>
            <Field label="Trial days">
              <input type="number" min="0" max="365" className="pm-input" value={form.pricing.trialDays} onChange={e => set('pricing|trialDays', e.target.value)} />
            </Field>
            <Field label="Grace period (days)" hint="After a lapsed renewal">
              <input type="number" min="0" max="90" className="pm-input" value={form.pricing.gracePeriodDays} onChange={e => set('pricing|gracePeriodDays', e.target.value)} />
            </Field>
            <Field label="Effective from">
              <input type="date" className="pm-input" value={form.effectiveFrom} onChange={e => set('effectiveFrom', e.target.value)} />
            </Field>
            <Field label="Effective until">
              <input type="date" className="pm-input" value={form.effectiveUntil} onChange={e => set('effectiveUntil', e.target.value)} />
            </Field>
          </div>
        </Section>

        <div className="pm-subhead">
          <h4>Entitlements</h4>
          <span className="pm-muted pm-small">{enabledCount} of {registry.features.length} features enabled · 0 means none allowed, tick Unlimited for no cap</span>
        </div>

        {groupContent.map(g => (
          <Section
            key={g.key}
            title={g.label}
            defaultOpen={false}
            aside={[
              g.features.length ? `${g.features.filter(d => form.features[d.key]).length}/${g.features.length} on` : null,
              [...g.limits, ...g.quotas].some(d => notes[d.key]?.tone === 'error') ? '⚠ conflict' : null,
            ].filter(Boolean).join(' · ') || null}
          >
            {g.features.length > 0 && (
              <div className="pm-feature-grid">
                {g.features.map(d => (
                  <Toggle key={d.key} label={d.label} checked={form.features[d.key]} onChange={v => set(`features|${d.key}`, v)} disabled={readOnly} />
                ))}
              </div>
            )}
            {g.limits.map(d => (
              <QuantityRow key={d.key} def={d} kind="limit" registry={registry} readOnly={readOnly} note={notes[d.key]}
                entry={form.limits[d.key]} onChange={v => set(`limits|${d.key}`, v)} />
            ))}
            {g.quotas.map(d => (
              <QuantityRow key={d.key} def={d} kind="quota" registry={registry} readOnly={readOnly} note={notes[d.key]}
                entry={form.quotas[d.key]} onChange={v => set(`quotas|${d.key}`, v)} />
            ))}
            {g.policies.map(d => (
              <div className="pm-qrow" key={d.key}>
                <div className="pm-qrow__label"><span>{d.label}</span><span className="pm-qrow__meta">policy</span></div>
                <div className="pm-qrow__controls">
                  <PolicyInput def={d} value={form.policies[d.key]} readOnly={readOnly} onChange={v => set(`policies|${d.key}`, v)} />
                </div>
              </div>
            ))}
          </Section>
        ))}

        <Section title="Pricing page & release notes" defaultOpen={false}>
          <Field label="Highlights" hint="One per line — shown on the pricing card">
            <textarea className="pm-input" rows={4} value={form.highlights} onChange={e => set('highlights', e.target.value)} />
          </Field>
          <div className="pm-grid pm-grid--2">
            <Field label="Terms version">
              <input className="pm-input" value={form.termsVersion} onChange={e => set('termsVersion', e.target.value)} />
            </Field>
            <Field label="Change note" hint="Internal — what changed in this version">
              <input className="pm-input" value={form.changeNote} onChange={e => set('changeNote', e.target.value)} maxLength={500} />
            </Field>
          </div>
        </Section>
      </fieldset>

      {confirm === 'publish' && (
        <ConfirmModal
          title={`Publish version ${version.version}?`}
          confirmLabel="Publish"
          busy={saving}
          onConfirm={runConfirmed}
          onClose={() => setConfirm(null)}
          message="Once published this version becomes immutable and is offered to new customers. Existing subscribers stay on their current version until you change their plan."
        >
          <ul className="pm-summary-list">
            <li>{form.pricing.billingOptions.filter(o => o.isActive).map(o => `${INTERVAL_LABELS[o.interval]}: ${formatMoney(toMinor(o.amount || 0))}`).join(' · ') || 'No active billing options'}</li>
            <li>{enabledCount} features enabled</li>
            <li>Restaurants: {form.limits['restaurants.max']?.unlimited ? 'Unlimited' : form.limits['restaurants.max']?.value} · Employees: {form.limits['employees.max']?.unlimited ? 'Unlimited' : form.limits['employees.max']?.value}</li>
            <li>Storage: {form.limits['media.storage.maxBytes']?.unlimited ? 'Unlimited' : formatBytes(fromDisplay({ unit: 'bytes' }, form.limits['media.storage.maxBytes']?.value))}</li>
          </ul>
          {warnings.length > 0 && (
            <div className="pm-callout pm-callout--warn">
              <strong>Check before publishing:</strong>
              <ul>{warnings.map(([key, n]) => <li key={key}><b>{labelOf(key)}</b>: {n.text}</li>)}</ul>
            </div>
          )}
        </ConfirmModal>
      )}
      {confirm === 'delete' && (
        <ConfirmModal title={`Delete draft v${version.version}?`} danger confirmLabel="Delete" busy={saving} onConfirm={runConfirmed} onClose={() => setConfirm(null)}
          message="The draft will be removed permanently. Version numbers are never reused." />
      )}
      {confirm === 'archive' && (
        <ConfirmModal title={`Archive v${version.version}?`} confirmLabel="Archive" busy={saving} onConfirm={runConfirmed} onClose={() => setConfirm(null)}
          message="Archived versions can no longer be assigned. Existing subscribers keep this version until their plan is changed." />
      )}
    </div>
  );
};

export default PlanVersionEditor;
